import { useCallback, useEffect, useState } from "react";
import { Check, Copy, Loader2, LogOut, MonitorSmartphone, ShieldCheck, ShieldOff, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

const btn = "flex h-9 items-center gap-1.5 rounded-full border border-zinc-300 px-4 text-xs transition-colors hover:border-zinc-900 disabled:opacity-50";
const fmt = (d) => (d ? new Date(d).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");

const BackupCodes = ({ codes, onDone }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => { await navigator.clipboard.writeText(codes.join("\n")); setCopied(true); };
  return (
    <div className="space-y-3" data-testid="backup-codes">
      <p className="text-sm text-zinc-600">Save these backup codes somewhere safe. Each one signs you in once if you lose your phone.</p>
      <div className="grid grid-cols-2 gap-1.5 rounded-md bg-zinc-100 p-3 font-mono text-sm">{codes.map((c) => <span key={c} data-testid="backup-code">{c}</span>)}</div>
      <div className="flex gap-2">
        <button onClick={copy} className={btn} data-testid="backup-codes-copy">{copied ? <Check size={13} /> : <Copy size={13} />}{copied ? "Copied" : "Copy"}</button>
        <button onClick={onDone} className="h-9 rounded-full bg-zinc-900 px-5 text-xs text-white" data-testid="backup-codes-done">I've saved them</button>
      </div>
    </div>
  );
};

/* Enable flow: password → QR + code → backup codes. */
const SetupDialog = ({ open, onClose, onEnabled }) => {
  const [step, setStep] = useState("password");
  const [pw, setPw] = useState("");
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [codes, setCodes] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setStep("password"); setPw(""); setSetup(null); setCode(""); setCodes(null); } }, [open]);
  const start = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const { data } = await api.post("/auth/2fa/setup", { password: pw }); setSetup(data); setStep("scan"); }
    catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };
  const enable = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const { data } = await api.post("/auth/2fa/enable", { code }); setCodes(data.backup_codes); setStep("codes"); onEnabled(); }
    catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="twofa-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-heading text-2xl">Two-factor authentication</DialogTitle></DialogHeader>
        {step === "password" && (
          <form onSubmit={start} className="space-y-3">
            <p className="text-sm text-zinc-600">You'll need an authenticator app (Google Authenticator, Authy, 1Password, Microsoft Authenticator…). Confirm your password to begin.</p>
            <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Current password" autoFocus data-testid="twofa-password" />
            <button type="submit" disabled={busy || !pw} className="h-10 w-full rounded-full bg-zinc-900 text-sm text-white disabled:opacity-50" data-testid="twofa-start">{busy ? <Loader2 size={14} className="mx-auto animate-spin" /> : "Continue"}</button>
          </form>
        )}
        {step === "scan" && setup && (
          <form onSubmit={enable} className="space-y-3">
            <p className="text-sm text-zinc-600">Scan this with your authenticator app, then enter the 6-digit code it shows.</p>
            <img src={setup.qr} alt="QR code" className="mx-auto h-44 w-44 rounded-md border border-zinc-200" data-testid="twofa-qr" />
            <details className="text-xs text-zinc-500"><summary className="cursor-pointer">Can't scan? Enter the key manually</summary><code className="mt-1 block break-all rounded bg-zinc-100 p-2 font-mono" data-testid="twofa-secret">{setup.secret}</code></details>
            <Input inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="123 456" className="text-center font-mono text-lg tracking-widest" data-testid="twofa-code" />
            <button type="submit" disabled={busy || code.replace(/\s/g, "").length < 6} className="h-10 w-full rounded-full bg-zinc-900 text-sm text-white disabled:opacity-50" data-testid="twofa-enable">{busy ? <Loader2 size={14} className="mx-auto animate-spin" /> : "Turn on two-factor"}</button>
          </form>
        )}
        {step === "codes" && codes && <BackupCodes codes={codes} onDone={onClose} />}
      </DialogContent>
    </Dialog>
  );
};

const DisableDialog = ({ open, onClose, onDisabled }) => {
  const [pw, setPw] = useState(""); const [code, setCode] = useState(""); const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setPw(""); setCode(""); } }, [open]);
  const go = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/auth/2fa/disable", { password: pw, code }); toast.success("Two-factor turned off"); onDisabled(); onClose(); }
    catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="twofa-disable-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-heading text-2xl">Turn off two-factor</DialogTitle></DialogHeader>
        <form onSubmit={go} className="space-y-3">
          <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Current password" data-testid="twofa-disable-password" />
          <Input inputMode="numeric" value={code} onChange={(e) => setCode(e.target.value)} placeholder="Authenticator or backup code" className="font-mono" data-testid="twofa-disable-code" />
          <button type="submit" disabled={busy || !pw || !code} className="h-10 w-full rounded-full bg-red-700 text-sm text-white disabled:opacity-50" data-testid="twofa-disable-confirm">{busy ? <Loader2 size={14} className="mx-auto animate-spin" /> : "Turn off"}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const SecuritySection = () => {
  const { user, refreshUser } = useAuth();
  const [sessions, setSessions] = useState(null);
  const [dlg, setDlg] = useState(null); // "setup" | "disable"
  const [codes, setCodes] = useState(null);
  const load = useCallback(() => api.get("/auth/sessions").then((r) => setSessions(r.data.sessions)).catch(() => setSessions([])), []);
  useEffect(() => { load(); }, [load]);
  const revoke = async (s) => {
    try { await api.delete(`/auth/sessions/${s.sid}`); toast.success(`Signed out ${s.device}`); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const revokeOthers = async () => {
    try { const { data } = await api.post("/auth/sessions/revoke-others"); toast.success(`Signed out ${data.revoked} other device${data.revoked === 1 ? "" : "s"}`); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const regen = async () => {
    const pw = window.prompt("Confirm your password to generate new backup codes");
    if (!pw) return;
    try { const { data } = await api.post("/auth/2fa/backup-codes", { password: pw }); setCodes(data.backup_codes); } catch (e) { toast.error(formatApiError(e)); }
  };
  const on = !!user?.totp_enabled;
  return (
    <section className="rounded-lg border border-zinc-200 bg-white p-8" data-testid="security-section">
      <h2 className="text-lg font-medium">Security</h2>
      <div className="mt-5 flex flex-wrap items-start justify-between gap-4 border-b border-zinc-100 pb-5">
        <div className="flex items-start gap-3">
          <div className={`flex h-10 w-10 items-center justify-center rounded-md ${on ? "bg-emerald-50 text-emerald-700" : "bg-zinc-100 text-zinc-500"}`}>{on ? <ShieldCheck size={18} /> : <ShieldOff size={18} />}</div>
          <div>
            <p className="font-medium" data-testid="twofa-status">Two-factor authentication · {on ? "On" : "Off"}</p>
            <p className="max-w-md text-xs text-zinc-500">{on ? "Signing in on a new device asks for a code from your authenticator app." : `A 6-digit code from your phone on top of your password.${user?.role === "owner" ? " Strongly recommended for owners." : ""}`}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {on ? (<>
            <button onClick={regen} className={btn} data-testid="twofa-regen"><KeyRound size={13} />New backup codes</button>
            <button onClick={() => setDlg("disable")} className={`${btn} border-red-200 text-red-700`} data-testid="twofa-disable">Turn off</button>
          </>) : <button onClick={() => setDlg("setup")} className="h-9 rounded-full bg-zinc-900 px-5 text-xs text-white" data-testid="twofa-setup">Turn on two-factor</button>}
        </div>
      </div>
      <div className="mt-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="flex items-center gap-2 font-medium"><MonitorSmartphone size={16} />Signed-in devices</p>
          {sessions && sessions.length > 1 && <button onClick={revokeOthers} className={btn} data-testid="sessions-revoke-others"><LogOut size={13} />Sign out other devices</button>}
        </div>
        {!sessions ? <Loader2 size={16} className="mt-3 animate-spin text-zinc-400" /> : (
          <ul className="mt-3 divide-y divide-zinc-100" data-testid="sessions-list">
            {sessions.map((s) => (
              <li key={s.sid} className="flex items-center justify-between gap-3 py-2.5 text-sm" data-testid={`session-${s.sid.slice(0, 8)}`} data-current={s.current ? "1" : undefined}>
                <div className="min-w-0">
                  <p className="truncate">{s.device}{s.current && <span className="ml-2 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700">This device</span>}</p>
                  <p className="text-xs text-zinc-500">{s.ip} · last active {fmt(s.last_seen)} · signed in {fmt(s.created_at)}</p>
                </div>
                {!s.current && <button onClick={() => revoke(s)} className="shrink-0 text-xs text-zinc-500 underline-offset-2 hover:text-red-700 hover:underline" data-testid={`session-revoke-${s.sid.slice(0, 8)}`}>Sign out</button>}
              </li>
            ))}
          </ul>
        )}
      </div>
      <SetupDialog open={dlg === "setup"} onClose={() => setDlg(null)} onEnabled={() => { refreshUser(); load(); }} />
      <DisableDialog open={dlg === "disable"} onClose={() => setDlg(null)} onDisabled={refreshUser} />
      <Dialog open={!!codes} onOpenChange={(o) => !o && setCodes(null)}>
        <DialogContent className="max-w-md" aria-describedby={undefined}><DialogHeader><DialogTitle className="font-heading text-2xl">New backup codes</DialogTitle></DialogHeader>{codes && <BackupCodes codes={codes} onDone={() => setCodes(null)} />}</DialogContent>
      </Dialog>
    </section>
  );
};
